/** @jsxImportSource @emotion/react */
import { CellContext, ColumnDef } from '@tanstack/react-table';
import { useContext, useEffect, useMemo, useState } from 'react';
import { Tooltip } from 'react-tooltip';

import { FcopyOverviewResponse, FolderData } from '../@types/api';
import { AppToken } from '../App';
import { ReactComponent as BlankIcon } from '../assets/images/blank-icon.svg';
import { ReactComponent as CopyIcon } from '../assets/images/copy-icon.svg';
import { ReactComponent as DocIcon } from '../assets/images/doc-icon.svg';
import { ReactComponent as DownArrowIcon } from '../assets/images/down-arrow.svg';
import { ReactComponent as FileIcon } from '../assets/images/file-icon.svg';
import { ReactComponent as FolderCloseIcon } from '../assets/images/folder-close.svg';
import { ReactComponent as FolderOpenIcon } from '../assets/images/folder-open.svg';
import { ReactComponent as ImgIcon } from '../assets/images/img-icon.svg';
import { ReactComponent as PdfIcon } from '../assets/images/pdf-icon.svg';
import { ReactComponent as XlsIcon } from '../assets/images/xls-icon.svg';
import {
  Button,
  FileBox,
  Line,
  NavigationText,
  Spinner,
  Text,
  notify,
} from '../components';
import { DEFAULT_LIMIT } from '../constants';
import { COLORS } from '../styles/colors';
import { FONT_WEIGHT } from '../styles/fonts';
import { getOverviewInfo } from '../utils/apiHelper';
import CreateFolderModal from './common/CreateFolderModel';
import TableTree from './common/TableTree';

const HomePage = () => {
  const [isShowSpinner, setIsShowSpinner] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [overviewResults, setOverviewResults] = useState<FcopyOverviewResponse>(
    [],
  );
  // const [limit, setLimit] = useState(DEFAULT_LIMIT);
  const [limit] = useState(DEFAULT_LIMIT);
  // const [offset, setOffSet] = useState(0);
  // const [totalRecords, setTotalRecords] = useState(0);
  const [isInit, setIsInit] = useState(true);
  const [showFileBox, setShowFileBox] = useState(false);
  const { authorizedToken } = JSON.parse(useContext(AppToken));
  const [folderId, setFolderId] = useState<number | null>(null);
  const [folderName, setFolderName] = useState<string | ''>();

  const headers = [
    {
      header: 'Name',
      accessor: 'name',
    },
    // {
    //   header: 'Upload by',
    //   accessor: 'updatedBy',
    // },
    {
      header: 'Upload date',
      accessor: 'updatedAt',
    },
  ];

  const columns = useMemo<ColumnDef<FolderData | undefined>[]>(() => {
    return [
      {
        header: () => (
          <span
            css={{
              fontWeight: FONT_WEIGHT.regular,
              fontSize: '14px',
              color: COLORS.black2,
            }}
          >
            {headers[0].header}
          </span>
        ),
        accessorKey: headers[0].accessor,
        cell: (c) =>
          c.row.original?.type === 'folder' ? (
            <div
              className="expander"
              style={{
                paddingLeft: `${c.row.depth * 1}rem`,
              }}
            >
              {c.row.getCanExpand() && c.row.original?.type === 'folder' ? (
                c.row.getIsExpanded() ? (
                  <>
                    <DownArrowIcon />{' '}
                    <FolderOpenIcon
                      onClick={c.row.getToggleExpandedHandler()}
                    />
                  </>
                ) : (
                  <>
                    <BlankIcon />{' '}
                    <FolderCloseIcon
                      onClick={c.row.getToggleExpandedHandler()}
                    />
                  </>
                )
              ) : (
                <>
                  <BlankIcon />{' '}
                  <FolderCloseIcon onClick={c.row.getToggleExpandedHandler()} />
                </>
              )}
              <span
                style={{ padding: '0 1.5rem', justifyContent: 'space-between' }}
              >
                {c.row.original?.name}
              </span>
            </div>
          ) : (
            <div
              style={{
                paddingLeft: `${c.row.depth * 1}rem`,
              }}
            >
              {c.row.original?.type === 'jpg' ||
              c.row.original?.type === 'jpeg' ||
              c.row.original?.type === 'png' ? (
                <div
                  style={{
                    padding: '0 1.15rem',
                    justifyContent: 'space-between',
                  }}
                >
                  <ImgIcon />{' '}
                  <span
                    style={{
                      padding: '0 1.25rem',
                      justifyContent: 'space-between',
                    }}
                  >
                    {c.row.original?.name}
                  </span>
                </div>
              ) : c.row.original?.type === 'pdf' ? (
                <div
                  style={{
                    padding: '0 1.15rem',
                    justifyContent: 'space-between',
                  }}
                >
                  <PdfIcon />
                  <span
                    style={{
                      padding: '0 1.25rem',
                      justifyContent: 'space-between',
                    }}
                  >
                    {c.row.original?.name}
                  </span>
                </div>
              ) : c.row.original?.type === 'doc' ||
                c.row.original?.type === 'docx' ? (
                <div
                  style={{
                    padding: '0 1.15rem',
                    justifyContent: 'space-between',
                  }}
                >
                  <DocIcon />
                  <span
                    style={{
                      padding: '0 1.25rem',
                      justifyContent: 'space-between',
                    }}
                  >
                    {c.row.original?.name}
                  </span>
                </div>
              ) : c.row.original?.type === 'xls' ||
                c.row.original?.type === 'xlsx' ? (
                <div
                  style={{
                    padding: '0 1.15rem',
                    justifyContent: 'space-between',
                  }}
                >
                  <XlsIcon />
                  <span
                    style={{
                      padding: '0 1.25rem',
                      justifyContent: 'space-between',
                    }}
                  >
                    {c.row.original?.name}
                  </span>
                </div>
              ) : (
                <div
                  style={{
                    padding: '0 1.15rem',
                    justifyContent: 'space-between',
                  }}
                >
                  <FileIcon />
                  <span
                    style={{
                      padding: '0 1.25rem',
                      justifyContent: 'space-between',
                    }}
                  >
                    {c.row.original?.name}
                  </span>
                </div>
              )}
            </div>
          ),
        size: 60,
      },
      ...headers.slice(1).map((header) => {
        return {
          header: () => (
            <span
              css={{
                fontWeight: FONT_WEIGHT.regular,
                fontSize: '14px',
                color: COLORS.black2,
              }}
            >
              {header.header}
            </span>
          ),
          accessorKey: header.accessor,
          size: 20,
        };
      }),
      {
        header: '',
        accessorKey: 'copyIcon',
        cell: (c: CellContext<FolderData | undefined, unknown>) =>
          c.row.original?.type != 'folder' ? (
            <>
              <CopyIcon
                id="copypath"
                data-tooltip-place="right"
                data-tooltip-content="Path copied"
                style={{ cursor: 'pointer' }}
                onClick={() => {
                  window.navigator.clipboard.writeText(
                    `S:\\${c.row.original?.path}` || '',
                  );
                }}
              />
              <Tooltip
                anchorSelect="#copypath"
                style={{ backgroundColor: COLORS.green4 }}
                openOnClick
              />
            </>
          ) : (
            ''
          ),
        size: 20,
        enableSorting: false,
      },
    ];
  }, []);

  useEffect(() => {
    const getData = async () => {
      setIsShowSpinner(true);
      // setTotalRecords(0);
      const { errorMessage, response } = await getOverviewInfo(
        {
          limit: limit.toString(),
          offset: '0',
        },
        authorizedToken,
      );

      if (errorMessage) {
        setIsShowSpinner(false);
        notify({
          message: errorMessage ?? 'Error while loading page',
          type: 'error',
        });
        return;
      }

      if (response) {
        setOverviewResults(response.data);
        // setTotalRecords(response?.totalRecords || 25);
        setIsShowSpinner(false);
      }
    };
    if (isInit) {
      getData();
      setIsInit(false);
      // setFolderId(null);
    }
  }, [isInit, isModalOpen]);

  // const fetchOverviewResults = async (request: FcopyOverviewRequest) => {
  //   setIsShowSpinner(true);
  //   setIsInit(false);
  //   if (request.limit) {
  //     setLimit(limit);
  //   }
  //   if (request.offset) {
  //     setOffSet(request.offset);
  //   }
  //   const modifiedRequest = {
  //     limit: request.limit?.toString() || limit.toString(),
  //     offset: request.offset?.toString() || offset.toString(),
  //   };

  //   const { errorMessage, response } = await getOverviewInfo(
  //     modifiedRequest,
  //     authorizedToken,
  //   );

  //   if (errorMessage) {
  //     setIsShowSpinner(false);
  //     notify({
  //       message: errorMessage ?? 'Error while loading page',
  //       type: 'error',
  //     });
  //     return;
  //   }

  //   if (response) {
  //     setOverviewResults(response.data);
  //     setTotalRecords(response?.totalRecords || 25);
  //     setIsShowSpinner(false);
  //   }
  // };

  const openPopup = () => setIsModalOpen(true);

  return (
    <>
      {isShowSpinner ? (
        <Spinner
          commonStyles={{
            alignItems: 'center',
            flex: 1,
          }}
        />
      ) : null}

      <div
        style={{
          width: 'auto',
          height: 'auto',
          opacity: isShowSpinner ? '0.5' : '1',
          pointerEvents: isShowSpinner ? 'none' : 'auto',
          position: 'relative',
        }}
      >
        <div
          style={{
            padding: '0 1rem',
            marginBottom: '1rem',
          }}
        >
          <NavigationText data="/ Target path files" />
          <div style={{ display: 'flex', flexDirection: 'row', width: '100%' }}>
            <div style={{ justifyContent: 'space-between' }}>
              <Text
                commonStyles={{
                  fontSize: '1.875rem',
                  color: COLORS.black,
                  fontWeight: FONT_WEIGHT.bold,
                }}
              >
                Target path files
              </Text>
            </div>
          </div>
        </div>
        <div
          style={{
            margin: '1.3125rem 0 0.3125rem 0',
            width: '100%',
            display: 'flex',
            flexDirection: 'row',
            padding: '0 1rem',
          }}
        >
          <Button
            id="create"
            title="Create a folder"
            type="submit"
            variant="secondary"
            style={{
              position: 'relative',
              float: 'inline-start',
              marginRight: '0.625rem',
              textAlign: 'center',
              // width: "10%",
              height: '45px',
              width: '150px',
            }}
            onClick={openPopup}
          />
          {isModalOpen && (
            <CreateFolderModal
              isOpen
              setIsOpen={setIsModalOpen}
              folderId={folderId}
              folderName={folderName}
              setIsInit={setIsInit}
            />
          )}
        </div>
        <div
          style={{
            padding: '0 1rem',
          }}
        >
          <Line
            commonStyles={{
              marginTop: '0.875rem',
              marginBottom: '0.8rem',
            }}
          />
        </div>
        <div
          style={{
            padding: '0 1rem',
            paddingTop: '0.5rem',
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            gridGap: 50,
          }}
        >
          <div style={{ gridTemplateColumns: '65px' }}>
            <TableTree
              columns={columns || []}
              data={overviewResults}
              setFolderId={setFolderId}
              setFolderName={setFolderName}
              setShowFileBox={setShowFileBox}
              showFileBox={showFileBox}
            />
          </div>
          {showFileBox ? (
            <div style={{ gridTemplateColumns: '35px' }}>
              <FileBox
                folderId={folderId}
                setFolderId={setFolderId}
                folderName={folderName}
                setIsInit={setIsInit}
              />
            </div>
          ) : null}
        </div>
      </div>
    </>
  );
};

export default HomePage;
